import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, switchMap } from "rxjs";
import { environment } from "../../../../environments/environment";

export interface UnsplashImage {
  id: string;
  created_at: string;
  updated_at: string;
  width: number;
  height: number;
  color: string;
  blur_hash: string;
  description: string | null;
  alt_description: string | null;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  links: {
    self: string;
    html: string;
    download: string;
    download_location: string;
  };
  user: {
    id: string;
    username: string;
    name: string;
    portfolio_url: string | null;
    bio: string | null;
    location: string | null;
    profile_image: {
      small: string;
      medium: string;
      large: string;
    };
    links: {
      self: string;
      html: string;
      photos: string;
      likes: string;
    };
  };
}

export interface UnsplashSearchResponse {
  total: number;
  total_pages: number;
  results: UnsplashImage[];
}

@Injectable({
  providedIn: "root",
})
export class UnsplashImageService {
  private readonly apiUrl = "https://api.unsplash.com";
  private readonly accessKey = environment.unsplashAccessKey;

  constructor(protected http: HttpClient) { }

  /**
   * Search for photos on Unsplash
   * @param query Search terms
   * @param page Page number to retrieve (default: 1)
   * @param perPage Number of items per page (default: 10, max: 30)
   * @param orderBy How to sort the photos (latest, relevant, views)
   * @param collections Collection ID('s) to narrow search
   * @param contentFilter Limit results by content safety (low, high)
   * @param color Filter results by color
   * @param orientation Filter by photo orientation (landscape, portrait, squarish)
   */
  searchPhotos(
    query: string,
    page: number = 1,
    perPage: number = 10,
    orderBy: "latest" | "relevant" = "relevant",
    collections?: string,
    contentFilter: "low" | "high" = "low",
    color?: "black_and_white" | "black" | "white" | "yellow" | "orange" | "red" | "purple" | "magenta" | "green" | "teal" | "blue",
    orientation?: "landscape" | "portrait" | "squarish"
  ): Observable<UnsplashSearchResponse> {
    let params = new HttpParams()
      .set("query", query)
      .set("page", page.toString())
      .set("per_page", perPage.toString())
      .set("order_by", orderBy)
      .set("content_filter", contentFilter);

    if (collections) {
      params = params.set("collections", collections);
    }

    if (color) {
      params = params.set("color", color);
    }

    if (orientation) {
      params = params.set("orientation", orientation);
    }

    const headers = new HttpHeaders({
      Authorization: `Client-ID ${this.accessKey}`,
    });

    return this.http.get<UnsplashSearchResponse>(`${this.apiUrl}/search/photos`, {
      params,
      headers,
    });
  }

  /**
   * Get a random photo from Unsplash
   * @param collections Public collection ID('s) to filter selection
   * @param featured Limit selection to featured photos
   * @param username Limit selection to a single user
   * @param query Limit selection to photos matching a search term
   * @param orientation Filter by photo orientation
   * @param contentFilter Limit results by content safety
   * @param count The number of photos to return (default: 1, max: 30)
   */
  getRandomPhoto(
    collections?: string,
    featured?: boolean,
    username?: string,
    query?: string,
    orientation?: "landscape" | "portrait" | "squarish",
    contentFilter: "low" | "high" = "low",
    count: number = 1
  ): Observable<UnsplashImage | UnsplashImage[]> {
    let params = new HttpParams().set("content_filter", contentFilter).set("count", count.toString());

    if (collections) {
      params = params.set("collections", collections);
    }

    if (featured !== undefined) {
      params = params.set("featured", featured.toString());
    }

    if (username) {
      params = params.set("username", username);
    }

    if (query) {
      params = params.set("query", query);
    }

    if (orientation) {
      params = params.set("orientation", orientation);
    }

    const headers = new HttpHeaders({
      Authorization: `Client-ID ${this.accessKey}`,
    });

    return this.http.get<UnsplashImage | UnsplashImage[]>(`${this.apiUrl}/photos/random`, {
      params,
      headers,
    });
  }

  /**
   * Get a single photo by ID
   * @param id The photo's ID
   */
  getPhoto(id: string): Observable<UnsplashImage> {
    const headers = new HttpHeaders({
      Authorization: `Client-ID ${this.accessKey}`,
    });

    return this.http.get<UnsplashImage>(`${this.apiUrl}/photos/${id}`, { headers });
  }

  /**
   * Track photo download (required by Unsplash API guidelines)
   * This must be called when a user downloads or uses a photo
   * @param downloadLocation The download_location from the photo object
   */
  trackDownload(downloadLocation: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Client-ID ${this.accessKey}`,
    });

    return this.http.get(downloadLocation, { headers });
  }

  /**
   * Download a photo (convenience method that also tracks the download)
   * @param photo The photo object to download
   * @param size The size to download (raw, full, regular, small, thumb)
   */
  downloadPhoto(photo: UnsplashImage, size: "raw" | "full" | "regular" | "small" | "thumb" = "regular"): void {
    // Track the download as required by Unsplash API guidelines
    this.trackDownload(photo.links.download_location).subscribe();

    // Open the image URL in a new tab or trigger download
    const url = photo.urls[size];
    window.open(url, "_blank");
  }

  translateToEnglish(query: string): Observable<string> {
    return this.http.post('https://nl.libretranslate.com/translate', {
      q: query,
      source: 'auto',
      target: 'en',
      format: 'text',
    }).pipe(map((res: any) => res.translatedText));
  }
}
